import * as z from "zod";

const messages = {
  requiredField: "Campo obrigatório",
  minCharacters: (num) => `Mínimo de ${num} caracteres`,
  maxCharacters: (num) => `Máximo de ${num} caracteres`,
  invalidEmail: "E-mail inválido",
  invalidURL: "URL inválida",
  invalidDate: "Data inválida",
};

// Teto do Resumo do Projeto: é o que cabe na coluna esquerda do slide da
// apresentação da seletiva (LIMITS.project_summary no backend). Exportado para
// os formulários usarem no maxLength e no contador de caracteres.
export const PROJECT_SUMMARY_MAX = 930;

export const ProjectsValidator = {
  responsible_name: z.string().min(2, { message: messages.minCharacters(2) }),
  email: z.string().email({ message: messages.invalidEmail }),
  role: z.string().min(2, { message: messages.minCharacters(2) }),
  address: z.string().min(5, { message: messages.minCharacters(5) }),
  neighbourhood: z.string().min(2, { message: messages.minCharacters(2) }),
  city: z.string().min(2, { message: messages.minCharacters(2) }),
  state: z.string().min(2, { message: messages.minCharacters(2) }),
  cep: z.string().min(8, { message: messages.minCharacters(8) }),
  phone: z.string().min(8, { message: messages.minCharacters(8) }),
  organization_type: z.string().min(2, { message: messages.minCharacters(2) }),
  other_organization_type: z
    .string()
    .min(2, { message: messages.minCharacters(2) })
    .optional()
    .nullable(),
  organization_area: z.string().min(2, { message: messages.minCharacters(2) }),
  project_name: z.string().min(5, { message: messages.minCharacters(5) }),
  // 930 é o que cabe na coluna esquerda do slide da apresentação da seletiva —
  // o backend valida o mesmo teto (validators/Project.js), aqui é só o eco.
  project_summary: z
    .string()
    .min(5, { message: messages.minCharacters(5) })
    .max(PROJECT_SUMMARY_MAX, {
      message: messages.maxCharacters(PROJECT_SUMMARY_MAX),
    }),
  resource_start_at: z
    .string({ message: messages.invalidDate })
    .min(1, messages.requiredField),
  resource_finish_at: z
    .string({ message: messages.invalidDate })
    .min(1, messages.requiredField),
  project_modality: z.string().min(2, { message: messages.minCharacters(2) }),
  project_history: z.string().min(5, { message: messages.minCharacters(5) }),
  main_goal: z.string().min(5, { message: messages.minCharacters(5) }),
  specific_goals: z.string().min(5, { message: messages.minCharacters(5) }),
  institution_history: z
    .string()
    .min(5, { message: messages.minCharacters(5) }),
  project_start_at: z
    .string({ message: messages.invalidDate })
    .min(1, messages.requiredField),
  project_finish_at: z
    .string({ message: messages.invalidDate })
    .min(1, messages.requiredField),
  pre_production_start_at: z
    .string({ message: messages.invalidDate })
    .min(1, messages.requiredField),
  pre_production_finish_at: z
    .string({ message: messages.invalidDate })
    .min(1, messages.requiredField),
  production_start_at: z
    .string({ message: messages.invalidDate })
    .min(1, messages.requiredField),
  production_finish_at: z
    .string({ message: messages.invalidDate })
    .min(1, messages.requiredField),
  pos_production_start_at: z
    .string({ message: messages.invalidDate })
    .min(1, messages.requiredField),
  pos_production_finish_at: z
    .string({ message: messages.invalidDate })
    .min(1, messages.requiredField),
  project_images: z.string().url().array({ message: messages.invalidURL }),
  project_links: z.string().nullable().optional(),
  project_commitments: z
    .string()
    .min(5, { message: messages.minCharacters(5) }),
  project_age_range: z.string().min(2, { message: messages.minCharacters(2) }),
  project_affirmative_actions: z
    .string()
    .min(5, { message: messages.minCharacters(5) }),
  project_social_classes: z
    .string()
    .min(5, { message: messages.minCharacters(5) }),
  project_direct_public_number: z.coerce
    .number({
      message: "Número inválido",
      invalid_type_error: "Número inválido",
    })
    .int({
      message: "Número inválido",
      invalid_type_error: "Número inválido",
    }),
  project_indirect_public_number: z.coerce
    .number({
      message: "Número inválido",
      invalid_type_error: "Número inválido",
    })
    .int({
      message: "Número inválido",
      invalid_type_error: "Número inválido",
    }),
  main_justification: z.string().min(5, { message: messages.minCharacters(5) }),
  project_need: z.string().min(5, { message: messages.minCharacters(5) }),
  project_relevance: z.string().min(5, { message: messages.minCharacters(5) }),
  project_result_indicators: z
    .string()
    .min(5, { message: messages.minCharacters(5) }),
  voluntary_action: z.string().min(5, { message: messages.minCharacters(5) }),
  disclosure_collaboration: z
    .string()
    .min(5, { message: messages.minCharacters(5) }),
  realization_address: z
    .string()
    .min(5, { message: messages.minCharacters(5) }),
  coverage: z.string().min(5, { message: messages.minCharacters(5) }),
  total_authorized_value: z.coerce
    .number({
      message: "Número inválido",
      invalid_type_error: "Número inválido",
    })
    .or(z.string()),
  investiment_value: z.coerce
    .number({
      message: "Número inválido",
      invalid_type_error: "Número inválido",
    })
    .or(z.string()),
  capture_balance: z.coerce
    .number({
      message: "Número inválido",
      invalid_type_error: "Número inválido",
    })
    .or(z.string()),
  other_investors: z.string().min(5, { message: messages.minCharacters(5) }),
  requested_value: z.coerce
    .number({
      message: "Número inválido",
      invalid_type_error: "Número inválido",
    })
    .or(z.string()),
  min_project_value: z.coerce
    .number({
      message: "Número inválido",
      invalid_type_error: "Número inválido",
    })
    .or(z.string()),
  visibility_tradeoffs: z
    .string()
    .min(5, { message: messages.minCharacters(5) }),
  relation_tradeoffs: z.string().min(5, { message: messages.minCharacters(5) }),
};
